/**
 * Tag-background colours for the categorical columns on the Attendee List
 * (`PunchupResponsesTable`). Each function maps a domain enum to a literal
 * hex string consumed by `<PunchupTag size="sm" />`.
 *
 * Hex values mirror the design tokens (see `src/styles/punchup-tokens.css`).
 * Full taxonomy and rationale: `src/styles/CLAUDE.md` → "Status colour taxonomy".
 *
 * Why hex literals (not CSS variables): `PunchupTag` resolves its background
 * via CSS `color-mix()` which only accepts a concrete colour at parse time.
 * If the underlying token changes, update the corresponding hex here.
 */

const COLOR_SUCCESS = '#7CC11F'; // lime — Completed / Sold / Scanned
const COLOR_INFO = '#3b82f6'; // blue — Pending / Transferred (informational)
const COLOR_WARNING = '#FACC15'; // yellow — Voided / Canceled (reversible cancellation)
const COLOR_DANGER = '#ef4444'; // red — Refunded / Rejected (destructive)
const COLOR_COMPED = '#14b8a6'; // teal — Comped (intentional gift, finalized positive)
const COLOR_SWITCHED = '#65a30d'; // olive — Switched To (terminal type-change)
const COLOR_BUYER = '#06b6d4'; // cyan — Buyer (active payer)
const COLOR_ATTEND = '#8b5cf6'; // violet — Attendee (categorical, distinct from Buyer)
const COLOR_INACTIVE = '#787B7E'; // grey — Not Scanned (no-show) / unknown

/**
 * Order status — same taxonomy as `Orders/components/OrdersTable/utils.ts`.
 * Keep the two helpers in sync if the backend grows new status values.
 */
/** Raw status the API surfaces for a ticket held by an unpaid pending upgrade. */
export const PENDING_UPGRADE_STATUS = 'pending_upgrade';

export const getResponseStatusBackground = (status: string): string => {
	switch (status) {
		case 'Completed':
		case 'Registered':
		case 'Sold':
		case 'Box Office (Card)':
		case 'Box Office (Cash)':
			return COLOR_SUCCESS;
		case 'Pending':
		case 'Transferred':
		case 'Transfer Pending':
		case 'Comp Pending':
		case 'External':
		case 'Switched From':
			return COLOR_INFO;
		case 'Voided':
		case 'Canceled':
		case 'Comp Canceled':
		case 'External Canceled':
		case PENDING_UPGRADE_STATUS:
			return COLOR_WARNING;
		case 'Refunded':
		case 'Rejected':
			return COLOR_DANGER;
		case 'Comped':
			return COLOR_COMPED;
		case 'Switched To':
			return COLOR_SWITCHED;
		default:
			return COLOR_INACTIVE;
	}
};

/**
 * Human label for a status tag. Statuses arrive display-ready from the API
 * except the derived pending-upgrade hold, which comes as its raw token.
 */
export const formatResponseStatusLabel = (status: string): string =>
	status === PENDING_UPGRADE_STATUS ? 'Upgrade Pending' : status;

/**
 * Ticket rows whose LATEST transaction may enter the switch workflow — must
 * mirror the API's isSwitchableTransactionType (which speaks transaction
 * types; these are their GetStatus display strings). Rows outside this set
 * (plus pending-upgrade holds) get Switch hidden instead of a doomed 409.
 *
 * `transferred` is admitted: an admin hand-off copies the sale's money onto
 * its row and names the sale as its predecessor, and the API's revenue walk
 * steps through that hop to the sale — so the seat switches, full-moves
 * and upcharges exactly like a sold one. `transfer pending` (a hand-off still
 * in flight) stays out; the API refuses it. A hand-off a FAN accepted also
 * reads `transferred` — the status string cannot tell the two apart — and the
 * API refuses that one with a 400 (the host shows its generic switch-failed
 * toast), because its chain reaches no paid sale.
 */
const SWITCHABLE_STATUSES = new Set([
	'sold',
	'box office (card)',
	'box office (cash)',
	'comped',
	'external',
	'switched from',
	'transferred',
]);

export const isSwitchableStatus = (status: string): boolean => SWITCHABLE_STATUSES.has(status.toLowerCase());

/** Lower-cased, with a raw `external_canceled` token read as its display string `external canceled`. */
const normalizeStatus = (status: string): string => status.toLowerCase().replace(/_/g, ' ');

/**
 * Canceled rows — a sale cancel (`Canceled`) and an import's cancel or void (`External Canceled`). Both
 * come back with Un-Cancel (the API restores an import to `External`), and neither is at the door.
 * `Comp Canceled` is not listed: the attendee list has never offered Un-Cancel on it.
 */
const CANCELED_STATUSES = new Set(['canceled', 'external canceled']);

export const isCanceledStatus = (status: string): boolean => CANCELED_STATUSES.has(normalizeStatus(status));

/**
 * Rows whose status shows the ticket was imported (TIC-952). Punchup never collected an import's money,
 * so the API refuses its refund and the row offers Void instead. An import that was later transferred or
 * switched reads `Transferred` / `Switched From`; the API's `imported` row flag covers those, and this
 * status read is the fallback for an API that doesn't send the flag yet.
 */
const IMPORTED_STATUSES = new Set(['external', 'external canceled']);

export const isImportedStatus = (status: string): boolean => IMPORTED_STATUSES.has(normalizeStatus(status));

/**
 * "Buyer" vs "Attendee" — both purely categorical (no inactive semantic).
 * Buyer = cyan (the one contact who paid for the order); Attendee = violet
 * (everyone else on the order). Avoiding grey here is intentional: every
 * attendee on an active order is *active*, not inactive — `getScannedBackground`
 * is the column that signals attendance state.
 */
export const getResponseTypeBackground = (isBuyer: boolean): string => (isBuyer ? COLOR_BUYER : COLOR_ATTEND);

/**
 * Scanned vs Not Scanned. Scanned = emerald, the "happy success" — ticket
 * made it through the gate. Not Scanned = grey, the canonical inactive
 * state — the ticket was unused (no-show) or has not yet been used. Once
 * the user is in the Attendee List they are typically post-event triaging
 * no-shows, where grey reads correctly as "this row didn't show".
 */
export const getScannedBackground = (isValidated: boolean): string => (isValidated ? COLOR_SUCCESS : COLOR_INACTIVE);
